import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { UserPlus, Shield, User, Eye, Trash2, Mail, RefreshCw, RotateCcw, Clock, Archive, ChevronDown, ChevronUp, Send, Crown, Link2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_CONFIG = {
  directeur: { label: 'Directeur', icon: Crown, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  administrateur: { label: 'Administrateur', icon: Shield, color: 'bg-red-100 text-red-700 border-red-200' },
  utilisateur: { label: 'Utilisateur', icon: User, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  visiteur: { label: 'Visiteur', icon: Eye, color: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const DURATION_OPTIONS = [
  { value: '24h', label: '24 heures' },
  { value: '48h', label: '48 heures' },
  { value: '1week', label: '1 semaine' },
  { value: '1month', label: '1 mois' },
];

const normalizeRole = (role) => role === 'admin' ? 'administrateur' : role === 'consultant' ? 'utilisateur' : role;

const RoleBadge = ({ role }) => {
  const nr = normalizeRole(role);
  const config = ROLE_CONFIG[nr] || ROLE_CONFIG.visiteur;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${config.color}`} data-testid="role-badge">
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
};

const AccessBadge = ({ expiresAt, isExpired }) => {
  if (!expiresAt) return <span className="text-xs text-gray-400">Pas de limite</span>;
  const date = new Date(expiresAt);
  const formatted = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${isExpired ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-testid="access-badge">
      <Clock className="h-3 w-3" />
      {isExpired ? 'Expiré' : formatted}
    </span>
  );
};

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [pendingLinks, setPendingLinks] = useState([]);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [renewDialogUser, setRenewDialogUser] = useState(null);
  const [renewDuration, setRenewDuration] = useState('1month');
  const [bodaccStatus, setBodaccStatus] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    email: '', firstName: '', lastName: '', role: 'utilisateur', accessDuration: '1month'
  });

  const headers = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, invitesRes, linksRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: headers() }),
        axios.get(`${API}/invitations`, { headers: headers() }),
        axios.get(`${API}/capital-links/pending`, { headers: headers() }).catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data);
      setInvitations(invitesRes.data);
      setPendingLinks(linksRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeUsers = users.filter(u => !u.is_deleted);
  const archivedUsers = users.filter(u => u.is_deleted);
  const pendingInvites = invitations.filter(i => !i.used);

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const now = new Date();
  const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
  const expiringSoon = activeUsers.filter(u => {
    if (!u.access_expires_at || u.auto_renew) return false;
    const exp = new Date(u.access_expires_at);
    const diff = exp - now;
    return diff > 0 && diff < THREE_DAYS;
  });
  const alreadyExpired = activeUsers.filter(u => {
    if (!u.access_expires_at || u.auto_renew) return false;
    return new Date(u.access_expires_at) < now;
  });

  const handleValidateLink = async (linkId) => {
    try {
      await axios.put(`${API}/capital-links/${linkId}/validate`, {}, { headers: headers() });
      setPendingLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success('Lien capitalistique valide');
    } catch (e) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleRejectLink = async (linkId) => {
    try {
      await axios.put(`${API}/capital-links/${linkId}/reject`, {}, { headers: headers() });
      setPendingLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success('Lien rejete');
    } catch (e) {
      toast.error('Erreur lors du rejet');
    }
  };

  const formatTimeLeft = (expiresAt) => {
    const diff = new Date(expiresAt) - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}j ${hours % 24}h`;
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email: inviteForm.email,
        first_name: inviteForm.firstName,
        last_name: inviteForm.lastName,
        role: inviteForm.role,
        access_duration: inviteForm.accessDuration,
      };
      const res = await axios.post(`${API}/users/invite`, payload, { headers: headers() });
      if (res.data.email_sent) {
        toast.success(res.data.message);
      } else {
        toast.warning(res.data.message);
      }
      setIsInviteOpen(false);
      setInviteForm({ email: '', firstName: '', lastName: '', role: 'utilisateur', accessDuration: '1month' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, { headers: headers() });
      toast.success('Role mis a jour');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSoftDelete = async (userId) => {
    if (!window.confirm('Desactiver cet utilisateur ? Il sera deplace dans les anciens utilisateurs.')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: headers() });
      toast.success('Utilisateur desactive');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRestore = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/restore`, {}, { headers: headers() });
      toast.success('Utilisateur reactive');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRenewAccess = async () => {
    if (!renewDialogUser) return;
    try {
      const res = await axios.patch(`${API}/users/${renewDialogUser.id}/access`, { duration: renewDuration }, { headers: headers() });
      toast.success(res.data.message);
      setRenewDialogUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleAutoRenew = async (userId, currentAutoRenew, currentDuration) => {
    try {
      const res = await axios.patch(`${API}/users/${userId}/auto-renew`, {
        auto_renew: !currentAutoRenew,
        duration: currentDuration || '1month',
      }, { headers: headers() });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const res = await axios.post(`${API}/invitations/${invitationId}/resend`, {}, { headers: headers() });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Supprimer cette invitation ?')) return;
    try {
      await axios.delete(`${API}/invitations/${invitationId}`, { headers: headers() });
      toast.success('Invitation supprimee');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const launchBodaccEnrichment = async () => {
    try {
      await axios.post(`${API}/admin/enrich-bodacc`, {}, { headers: headers() });
      toast.success('Enrichissement BODACC lance');
      checkBodaccStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const checkBodaccStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/enrich-bodacc/status`, { headers: headers() });
      setBodaccStatus(res.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    checkBodaccStatus();
    const interval = setInterval(checkBodaccStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#f1f5f9]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }} data-testid="admin-title">
              Administration
            </h1>
            <p className="text-muted-foreground mt-1">
              {activeUsers.length} actif{activeUsers.length > 1 ? 's' : ''} &middot; {pendingInvites.length} invitation{pendingInvites.length > 1 ? 's' : ''} &middot; {archivedUsers.length} archive{archivedUsers.length > 1 ? 's' : ''}
            </p>
          </div>

          <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 rounded-full" data-testid="invite-btn">
                <UserPlus className="h-4 w-4" />
                Inviter
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Inviter un utilisateur</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Prenom</Label>
                    <Input value={inviteForm.firstName} onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })} placeholder="Marie" required data-testid="invite-firstname-input" />
                  </div>
                  <div>
                    <Label>Nom</Label>
                    <Input value={inviteForm.lastName} onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })} placeholder="Martin" required data-testid="invite-lastname-input" />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="marie@example.com" required data-testid="invite-email-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Role</Label>
                    <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                      <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="administrateur">Administrateur</SelectItem>
                        <SelectItem value="utilisateur">Utilisateur</SelectItem>
                        <SelectItem value="visiteur">Visiteur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Duree d'acces</Label>
                    <Select value={inviteForm.accessDuration} onValueChange={(v) => setInviteForm({ ...inviteForm, accessDuration: v })}>
                      <SelectTrigger data-testid="invite-duration-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DURATION_OPTIONS.map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  L'utilisateur devra s'inscrire avec exactement le meme email, prenom et nom.
                </p>
                <Button type="submit" className="w-full rounded-full" data-testid="send-invite-btn">
                  Envoyer l'invitation
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Expiration Alerts */}
        {(expiringSoon.length > 0 || alreadyExpired.length > 0) && (
          <div className="rounded-xl border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-red-50 p-5 mb-6" data-testid="expiration-alerts">
            <h2 className="font-heading font-semibold text-orange-800 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Suivi des expirations
            </h2>
            <div className="space-y-2">
              {alreadyExpired.map(user => (
                <div key={user.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-red-200">
                  <div className="flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-sm font-medium">{user.name}</span>
                    <span className="text-xs text-red-600 font-semibold bg-red-50 px-2 py-0.5 rounded-full">Expire</span>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1 text-xs border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => { setRenewDialogUser(user); setRenewDuration(user.access_duration || '1month'); }} data-testid="alert-renew-btn">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Renouveler maintenant
                  </Button>
                </div>
              ))}
              {expiringSoon.map(user => (
                <div key={user.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-orange-200">
                  <div className="flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full bg-orange-400 animate-pulse" />
                    <span className="text-sm font-medium">{user.name}</span>
                    <span className="text-xs text-orange-600 font-semibold bg-orange-50 px-2 py-0.5 rounded-full">Expire dans {formatTimeLeft(user.access_expires_at)}</span>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1 text-xs border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => { setRenewDialogUser(user); setRenewDuration(user.access_duration || '1month'); }} data-testid="alert-renew-btn">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Renouveler
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BODACC Enrichment */}
        <div className="bg-white border border-border/50 rounded-xl p-5 mb-6 shadow-sm" data-testid="bodacc-section">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-semibold text-gray-700 flex items-center gap-2">
                Situations juridiques (BODACC)
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Detecte les entreprises en RJ/LJ, plan de sauvegarde ou continuation via le BODACC
              </p>
            </div>
            <div className="flex items-center gap-3">
              {bodaccStatus && bodaccStatus.running && (
                <div className="text-xs text-muted-foreground">
                  <RefreshCw className="h-3 w-3 inline animate-spin mr-1" />
                  {bodaccStatus.processed}/{bodaccStatus.total} ({Math.round(bodaccStatus.processed / Math.max(bodaccStatus.total, 1) * 100)}%)
                </div>
              )}
              {bodaccStatus && !bodaccStatus.running && bodaccStatus.total > 0 && (
                <span className="text-xs text-emerald-600">Dernier scan: {bodaccStatus.processed}/{bodaccStatus.total} traites</span>
              )}
              <Button
                size="sm"
                onClick={launchBodaccEnrichment}
                disabled={bodaccStatus?.running}
                className="gap-2 rounded-full bg-[#1A4D2E] text-white hover:bg-[#153d24]"
                data-testid="bodacc-enrich-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${bodaccStatus?.running ? 'animate-spin' : ''}`} />
                {bodaccStatus?.running ? 'En cours...' : 'Scanner BODACC'}
              </Button>
            </div>
          </div>
        </div>

        {/* Pending Capital Links */}
        {pendingLinks.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 mb-6" data-testid="pending-links">
            <h2 className="font-heading font-semibold text-indigo-800 mb-3 flex items-center gap-2">
              Liens capitalistiques en attente ({pendingLinks.length})
            </h2>
            <div className="space-y-2">
              {pendingLinks.map(link => (
                <div key={link.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-indigo-100">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{link.source_company_name}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-200">{link.link_type}</span>
                      <span className="text-sm font-semibold">{link.target_company_name}</span>
                      {link.percentage && <span className="text-xs text-muted-foreground">({link.percentage}%)</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">Soumis par {link.created_by}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <Button size="sm" onClick={() => handleValidateLink(link.id)} className="bg-emerald-600 text-white hover:bg-emerald-700 h-7 text-xs rounded-full" data-testid="validate-link-btn">
                      Valider
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleRejectLink(link.id)} className="h-7 text-xs rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-link-btn">
                      Rejeter
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Invitations */}
        {pendingInvites.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6" data-testid="pending-invitations">
            <h2 className="font-heading font-semibold text-amber-800 mb-3 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Invitations en attente ({pendingInvites.length})
            </h2>
            <div className="space-y-2">
              {pendingInvites.map(inv => (
                <div key={inv.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-amber-100">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-sm font-medium truncate">{inv.first_name} {inv.last_name}</span>
                    <span className="text-xs text-muted-foreground truncate">{inv.email}</span>
                    <RoleBadge role={inv.role} />
                    {inv.access_duration && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                        {DURATION_OPTIONS.find(d => d.value === inv.access_duration)?.label || inv.access_duration}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 gap-1 text-xs" onClick={() => handleResendInvitation(inv.id)} data-testid="resend-invite-btn">
                      <Send className="h-3.5 w-3.5" />
                      Relancer
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8" onClick={() => handleDeleteInvitation(inv.id)} data-testid="delete-invite-btn">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Users */}
        <div className="bg-white rounded-xl border border-border/50 shadow-sm overflow-hidden mb-6" data-testid="users-list">
          <div className="px-5 py-3 bg-accent/10 border-b border-border/50">
            <h2 className="font-heading font-semibold text-gray-700">Utilisateurs actifs ({activeUsers.length})</h2>
          </div>
          <div className="divide-y divide-border/50">
            {activeUsers.map(user => {
              const expired = isExpired(user.access_expires_at);
              return (
                <div key={user.id} className={`px-5 py-4 ${expired ? 'bg-red-50/40' : ''}`} data-testid="user-row">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-semibold text-gray-900">{user.name}</p>
                        <RoleBadge role={user.role} />
                        <AccessBadge expiresAt={user.access_expires_at} isExpired={expired} />
                        {user.auto_renew && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200" data-testid="auto-renew-badge">
                            <RefreshCw className="h-3 w-3" />
                            Auto
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Select value={normalizeRole(user.role)} onValueChange={(v) => handleRoleChange(user.id, v)}>
                        <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="administrateur">Administrateur</SelectItem>
                          <SelectItem value="utilisateur">Utilisateur</SelectItem>
                          <SelectItem value="visiteur">Visiteur</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" className="gap-1 text-xs h-8" onClick={() => { setRenewDialogUser(user); setRenewDuration(user.access_duration || '1month'); }} data-testid="renew-btn">
                        <Clock className="h-3.5 w-3.5" />
                        Renouveler
                      </Button>
                      <div className="flex items-center gap-1.5" title={user.auto_renew ? 'Renouvellement auto actif' : 'Renouvellement auto inactif'}>
                        <Switch checked={user.auto_renew || false} onCheckedChange={() => handleToggleAutoRenew(user.id, user.auto_renew, user.access_duration)} data-testid="auto-renew-switch" />
                      </div>
                      <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8" onClick={() => handleSoftDelete(user.id)} data-testid="delete-user-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            {activeUsers.length === 0 && (
              <div className="px-5 py-8 text-center text-muted-foreground">Aucun utilisateur actif</div>
            )}
          </div>
        </div>

        {/* Archived Users */}
        {archivedUsers.length > 0 && (
          <div className="bg-white rounded-xl border border-border/50 shadow-sm overflow-hidden" data-testid="archived-users">
            <button
              className="w-full px-5 py-3 bg-gray-50 border-b border-border/50 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setShowArchived(!showArchived)}
              data-testid="toggle-archived-btn"
            >
              <h2 className="font-heading font-semibold text-gray-500 flex items-center gap-2">
                <Archive className="h-4 w-4" />
                Anciens utilisateurs ({archivedUsers.length})
              </h2>
              {showArchived ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </button>
            {showArchived && (
              <div className="divide-y divide-border/50">
                {archivedUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between px-5 py-4 bg-gray-50/50" data-testid="archived-user-row">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <p className="font-semibold text-gray-500">{user.name}</p>
                        <RoleBadge role={user.role} />
                        <span className="text-xs text-gray-400">
                          Desactive le {user.deleted_at ? new Date(user.deleted_at).toLocaleDateString('fr-FR') : '—'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{user.email}</p>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => handleRestore(user.id)} data-testid="restore-user-btn">
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reactiver
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Renew Dialog */}
        <Dialog open={!!renewDialogUser} onOpenChange={(open) => !open && setRenewDialogUser(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">Renouveler l'acces</DialogTitle>
            </DialogHeader>
            {renewDialogUser && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Renouveler l'acces de <strong>{renewDialogUser.name}</strong> a partir de maintenant.
                </p>
                <div>
                  <Label>Duree</Label>
                  <Select value={renewDuration} onValueChange={setRenewDuration}>
                    <SelectTrigger data-testid="renew-duration-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DURATION_OPTIONS.map(d => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleRenewAccess} className="w-full rounded-full" data-testid="confirm-renew-btn">
                  Confirmer le renouvellement
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Admin;
